import type {GridSortModel} from '@mui/x-data-grid';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {useAsyncFn} from 'react-use';
import {getAllForestMassStatusesUseCase, getCurrentUserUseCase} from '@/compositionRoot';
import type {ForestMassStatus} from '@/domain/entities/ForestMassStatus';
import UserPermissions from '@/domain/useCases/userPermissions';

const ALLOWED_SORT_FIELDS = new Set([
  'createdAt',
  'code',
  'concello',
  'appraiserName',
  'plannedCuttingDate',
  'finsaStatus',
  'aseforStatus',
  'fortopStatus',
]);

interface ControllerState {
  forestMassesStatuses: ForestMassStatus[];
  loading: boolean;
  error: string | null;
}

export const useController = () => {
  const [state, setState] = useState<ControllerState>({
    forestMassesStatuses: [],
    loading: true,
    error: null,
  });

  const [user, fetchUser] = useAsyncFn(() => getCurrentUserUseCase.execute(), []);
  const [searchParams, setSearchParams] = useSearchParams();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Not necessary
  useEffect(() => {
    getAllForestMassStatusesUseCase
      .execute({})
      .then((forestMassStatuses) => setState({forestMassesStatuses: forestMassStatuses, loading: false, error: null}))
      .catch((e) => setState({forestMassesStatuses: [], loading: false, error: e.message}));

    fetchUser();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const handleAdd = () => {
    navigate('upload');
  };

  const handleSelectForestMass = (uuid: string) => {
    navigate(uuid, {state: {from: `${location.pathname}${location.search}`}});
  };

  const userPermissions = useMemo(() => new UserPermissions(user.value), [user.value]);

  const sortModel = useMemo<GridSortModel>(() => {
    for (const [field, direction] of searchParams.entries()) {
      if (!ALLOWED_SORT_FIELDS.has(field)) continue;
      if (direction !== 'ASC' && direction !== 'DESC') continue;
      return [{field, sort: direction.toLowerCase() as 'asc' | 'desc'}];
    }
    return [];
  }, [searchParams]);

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSearchParams(
        (prev) => {
          for (const field of ALLOWED_SORT_FIELDS) prev.delete(field);
          const first = model[0];
          if (first && (first.sort === 'asc' || first.sort === 'desc')) {
            prev.set(first.field, first.sort.toUpperCase());
          }
          return prev;
        },
        {replace: true},
      );
    },
    [setSearchParams],
  );

  return {
    ...state,
    userPermissions,
    onAdd: handleAdd,
    onSelectForestMass: handleSelectForestMass,
    sortModel,
    onSortModelChange,
  };
};
